import { Container, Graphics, Text } from 'pixi.js';
import { theme } from '../config/theme';
import { Beacon } from './beacon';

export interface AgentDef {
  did: string;
  kind: 'ghost' | 'real';
  trustScore: number;
  credentials: string[];
  x: number;
  y: number;
}

export class Agent extends Container {
  public did: string;
  public kind: 'ghost' | 'real';
  public trustScore: number;
  public credentials: string[];
  public vx = 0;
  public vy = 0;

  private gfx: Graphics;
  private aura: Graphics;
  private scoreLabel: Text;
  private trail: Array<{ x: number; y: number; t: number }> = [];
  public targetBeacon: Beacon | null = null;
  

  constructor(def: AgentDef) {
    super();
    this.did = def.did;
    this.kind = def.kind;
    this.trustScore = def.trustScore;
    this.credentials = def.credentials;
    this.x = def.x;
    this.y = def.y;

    this.gfx = new Graphics();
    this.drawBody();
    this.addChild(this.gfx);

    this.aura = new Graphics();
    this.drawAura();
    this.addChild(this.aura);

    // Trust score label
    this.scoreLabel = new Text({
      text: `${this.trustScore}`,
      style: {
        fontFamily: theme.fonts.mono,
        fontSize: 8,
        fill: theme.colors.label,
        align: 'center',
      },
    });
    this.scoreLabel.anchor.set(0.5, 0.5);
    this.scoreLabel.y = -theme.sizes.agentRadius - 8;
    this.addChild(this.scoreLabel);
  }

  private drawBody() {
    const color = this.kind === 'real' ? theme.colors.agentReal : theme.colors.agentGhost;
    const alpha = this.kind === 'real' ? 1 : theme.colors.agentGhostOpacity;

    this.gfx.clear();
    this.gfx.fill({ color, alpha });
    this.gfx.circle(0, 0, theme.sizes.agentRadius);
    this.gfx.fill();

    // Glow ring
    this.gfx.stroke({ color, width: 1, alpha: alpha * 0.5 });
    this.gfx.circle(0, 0, theme.sizes.agentRadius + 2);
    this.gfx.stroke();
  }

  private drawAura() {
    const r = (this.trustScore / 100) * theme.sizes.auraMaxRadius;
    const alpha = this.kind === 'real' ? 0.15 : 0.05;
    const color = this.kind === 'real' ? theme.colors.agentReal : theme.colors.agentGhost;

    this.aura.clear();
    this.aura.fill({ color, alpha });
    this.aura.circle(0, 0, r);
    this.aura.fill();
  }

  setTarget(beacon: Beacon | null) {
    this.targetBeacon = beacon;
  }

  tick(_dt: number) {
    const maxSpeed = this.kind === 'real' ? theme.animation.realAgentMaxSpeed : theme.animation.agentMaxSpeed;

    if (this.targetBeacon) {
      const dx = this.targetBeacon.x - this.x;
      const dy = this.targetBeacon.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 1) {
        this.vx += (dx / dist) * theme.animation.acceleration;
        this.vy += (dy / dist) * theme.animation.acceleration;
      }
    } else {
      // Brownian drift
      this.vx += (Math.random() - 0.5) * 0.05;
      this.vy += (Math.random() - 0.5) * 0.05;
    }

    // Clamp speed
    const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    if (speed > maxSpeed) {
      this.vx = (this.vx / speed) * maxSpeed;
      this.vy = (this.vy / speed) * maxSpeed;
    }

    // Friction
    this.vx *= theme.animation.friction;
    this.vy *= theme.animation.friction;

    this.x += this.vx;
    this.y += this.vy;

    // Trail
    this.trail.push({ x: this.x, y: this.y, t: Date.now() });
    this.trail = this.trail.filter(p => Date.now() - p.t < theme.animation.trailDecayMs);
  }

  drawTrail(g: Graphics, _w: number, _h: number) {
    if (this.trail.length < 2) return;
    const now = Date.now();
    for (let i = 1; i < this.trail.length; i++) {
      const p1 = this.trail[i - 1];
      const p2 = this.trail[i];
      const age = (now - p2.t) / theme.animation.trailDecayMs;
      const alpha = (1 - age) * (this.kind === 'real' ? 0.8 : 0.3);
      const color = this.kind === 'real' ? theme.colors.agentReal : theme.colors.agentGhost;
      g.stroke({ color, width: 1, alpha });
      g.moveTo(p1.x, p1.y);
      g.lineTo(p2.x, p2.y);
      g.stroke();
    }
  }

  isAtBeacon(): boolean {
    if (!this.targetBeacon) return false;
    const dx = this.targetBeacon.x - this.x;
    const dy = this.targetBeacon.y - this.y;
    const r = this.targetBeacon.def.isPort ? theme.sizes.portRadius : theme.sizes.beaconRadius;
    return dx * dx + dy * dy < r * r;
  }

  getLabelText(): string {
    return this.did.split(':').pop() || this.did;
  }
}
